import React, { useEffect, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';

const MAX_LEN = 200;

interface EditableDescriptionProps {
  value: string;
  isEdited: boolean;
  onCommit: (next: string) => void;
  ariaLabel?: string;
}

export const EditableDescription: React.FC<EditableDescriptionProps> = ({
  value,
  isEdited,
  onCommit,
  ariaLabel = 'Descrição',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const committedRef = useRef(false);

  useEffect(() => {
    if (isEditing) {
      committedRef.current = false;
      setDraft(value);
    }
  }, [isEditing, value]);

  useEffect(() => {
    if (!isEditing) return;
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [isEditing]);

  const enter = () => setIsEditing(true);

  const commit = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    const trimmed = draft.trim();
    if (trimmed.length > 0 && trimmed !== value) {
      onCommit(trimmed);
    }
    setIsEditing(false);
  };

  const cancel = () => {
    committedRef.current = true;
    setDraft(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        aria-label={ariaLabel}
        value={draft}
        maxLength={MAX_LEN}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commit}
        className="min-w-0 flex-1 h-7 px-1.5 -mx-1.5 text-sm bg-transparent border-0 border-b border-border focus:border-primary/60 focus:outline-none text-foreground"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={enter}
      title={isEdited ? 'Descrição editada — clique para alterar' : 'Clique para editar'}
      className="group/desc flex items-center gap-1.5 min-w-0 flex-1 text-left text-sm text-foreground hover:bg-muted/30 rounded-md px-1.5 -mx-1.5 py-0.5 -my-0.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
    >
      <span className="truncate min-w-0 flex-1">{value}</span>
      <Pencil
        className={cn(
          'h-3 w-3 text-muted-foreground shrink-0 transition-opacity',
          isEdited
            ? 'opacity-50'
            : 'opacity-0 group-hover/desc:opacity-50 group-focus-visible/desc:opacity-50',
        )}
        aria-hidden="true"
      />
    </button>
  );
};
